import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { CaseInsensitiveMap } from "../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { InvalidOperationException } from "../../../Global/Base/Exceptions";
import { CryptoKeyUsage, IWebCryptoAlgSettings, SecurityUtils } from "../../../Global/Base/Security/SecurityUtils";
import { IWebCryptoProviderSettings } from "./IEncryptionProviderSettings";

declare let forge: any; //deklarace externí knihovny forge

const logger = LazyLoadingSectionLogger.create(LoggingSection.encryption);

/**
 * Poskytuje providery pro šifrování a dešifrování
 */
export class EncryptionProviderManager {
    protected static _providers: CaseInsensitiveMap<IWebCryptoProviderSettings>;

    public static registerProvider(provider: IWebCryptoProviderSettings, replaceIfExists: boolean = false): void {
        if (!this._providers)
            this._providers = new CaseInsensitiveMap<IWebCryptoProviderSettings>();
        if (this._providers.has(provider.algorithmName) && !replaceIfExists)
            logger.info(`provider: ${provider.algorithmName} is already registered.`); //`
        else
            this._providers.set(provider.algorithmName, provider);
    }

    /**
     * Vrací provider pro šifrování a podle názvu providera.
     * @param type Název provideru (odpovídající specifikaci )
     * @param requiredAction požadovaná krypto operace
     */
    public static getEncryptionProviderByType(type: string, requiredAction: CryptoKeyUsage): IWebCryptoProviderSettings {
        if (type == null || type.toUpperCase() === "RSA") //z důvodu zpětné kompatibility. U starších záznamů není provider uveden, nebo je uvedenou pouze RSA.)
            type = "RSA1_5";

        if (!this._providers || (this._providers && this._providers.size === 0))
            throw new InvalidOperationException(`Failed to get encryption provider '${type}'. No provider is registered.`); //`

        const prov = this._providers.get(type);

        if (prov == null)
            throw new InvalidOperationException(`Failed to get encryption provider '${type}'. Provider is not registered.`); //`
        else if (prov.allowedUsage.indexOf(requiredAction) === -1)
            throw new InvalidOperationException(`Failed to get encryption provider '${type}'. Provider does not allow requested usage '${requiredAction}'.`); //`
        else
            return prov;
    }
}

abstract class ACryptoProvider implements IWebCryptoProviderSettings {
    public readonly algorithmName: string;
    public readonly keyType: string;
    public readonly allowedUsage: CryptoKeyUsage[];
    public readonly webCryptoAlgSettings: IWebCryptoAlgSettings;

    constructor(algName: string, kty: string, webCryptoAlgName: string, usage: CryptoKeyUsage[], hashAlg?: string) {
        this.algorithmName = algName;
        this.keyType = kty;
        this.allowedUsage = usage;
        this.webCryptoAlgSettings = { name: webCryptoAlgName };
        if (hashAlg)
            this.webCryptoAlgSettings.hash = { name: hashAlg };
    }

    public async encryptBuffer(buffer: ArrayBuffer, jwk: JsonWebKey): Promise<ArrayBuffer> {
        const asymKey = await SecurityUtils.importKey(jwk, this.webCryptoAlgSettings, ["encrypt"]);

        return await self.crypto.subtle.encrypt({ name: asymKey.algorithm.name! }, asymKey, buffer);
    }
}

// PODPOROVANE PROVIDERY
// http://www.w3.org/TR/WebCryptoAPI/#jwk-mapping-alg
// https://tools.ietf.org/html/rfc7518#appendix-A.2

//RSA PKCS WebCrypto usage: generateKey | importKey | exportKey | sign | verify

type Forge = typeof import("node-forge");

/**Obecná třída pro šifrování pomocí knihovny forge. */
class RsaForgeCryptoProvider extends ACryptoProvider {
    private __forge?: Promise<Forge>; //externí knihovna https://github.com/digitalbazaar/forge

    private async _getForge(): Promise<any> {
        if (!this.__forge)
            this.__forge = import("node-forge").then(module => module.default);
        return this.__forge;
    }

    constructor(algName: string, kty: string, webCryptoAlgName: string, usage: CryptoKeyUsage[], hashAlg: string) {
        super(algName, kty, webCryptoAlgName, usage, hashAlg);
    }

    private async _base64urlToBigInteger(str: string): Promise<ArrayBuffer> {
        await this._getForge();
        const bytes = forge.util.decode64((str + "===".slice((str.length + 3) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
        return new forge.jsbn.BigInteger(forge.util.bytesToHex(bytes), 16);
    }

    public async _encryptBuffer(buffer: ArrayBuffer, jwk: JsonWebKey): Promise<ArrayBuffer> {
        await this._getForge();
        const publicKey = forge.pki.setRsaPublicKey(await this._base64urlToBigInteger(jwk.n || ""), await this._base64urlToBigInteger(jwk.e || ""));
        // INFO: nepoužívat `forge.util.binary.raw.encode`, jelikož nezvládne větší blok jak ~100kb
        const bytes = SecurityUtils.arrayToBinaryEncodedString(new Uint8Array(buffer));
        return publicKey.encrypt(bytes, this.webCryptoAlgSettings.name);
    }

    /**
       * Zakóduje data do UTF8 a zašifruje zadaným klíčem
       * @param bytes data pro zašifrování
       * @param key šifrovací klíč
       */
    public override async encryptBuffer(buffer: ArrayBuffer, jwk: JsonWebKey): Promise<ArrayBuffer> {
        await this._getForge();
        return forge.util.binary.raw.decode(await this._encryptBuffer(buffer, jwk)).buffer;
    }
}

class RSA1_5 extends RsaForgeCryptoProvider {
    constructor() {
        super("RSA1_5", "RSA", "RSAES-PKCS1-v1_5", ["encrypt", "decrypt"], "SHA-1");
    }
}

class RS256 extends RsaForgeCryptoProvider {
    constructor() {
        super("RS256", "RSA", "RSAES-PKCS1-v1_5", ["decrypt"], "SHA-256"); //encrypt by bylo možné, použít ale momentálně chybí podpora v SL a CNA
    }
}
class RS384 extends RsaForgeCryptoProvider {
    constructor() {
        super("RS384", "RSA", "RSAES-PKCS1-v1_5", ["decrypt"], "SHA-384"); //encrypt by bylo možné, použít ale momentálně chybí podpora v SL a CNA
    }
}
class RS512 extends RsaForgeCryptoProvider {
    constructor() {
        super("RS512", "RSA", "RSAES-PKCS1-v1_5", ["decrypt"], "SHA-512"); //encrypt by bylo možné, použít ale momentálně chybí podpora v SL a CNA
    }
}

//RSA PSS WebCrypto usage: generateKey | importKey | exportKey | sign | verify
class PS256 extends ACryptoProvider {
    constructor() {
        super("PS256", "RSA", "RSA-PSS", [], "SHA-256");
    }
}

class PS384 extends ACryptoProvider {
    constructor() {
        super("PS384", "RSA", "RSA-PSS", [], "SHA-384");
    }
}

class PS512 extends ACryptoProvider {
    constructor() {
        super("PS512", "RSA", "RSA-PSS", [], "SHA-512");
    }
}

//RSA OAEP WebCrypto usage: generateKey | importKey | exportKey | encrypt | decrypt | wrapKey | unwrapKey
class RSA_OAEP extends ACryptoProvider {
    constructor() {
        super("RSA-OAEP", "RSA", "RSA-OAEP", ["encrypt", "decrypt"], "SHA-1");
    }
}

class RSA_OAEP_256 extends ACryptoProvider {
    constructor() {
        super("RSA-OAEP-256", "RSA", "RSA-OAEP", ["encrypt", "decrypt"], "SHA-256");
    }
}

class RSA_OAEP_384 extends ACryptoProvider {
    constructor() {
        super("RSA-OAEP-384", "RSA", "RSA-OAEP", ["encrypt", "decrypt"], "SHA-384");
    }
}

class RSA_OAEP_512 extends ACryptoProvider {
    constructor() {
        super("RSA-OAEP-512", "RSA", "RSA-OAEP", ["encrypt", "decrypt"], "SHA-512");
    }
}

//EC WebCrypto usage: generateKey | importKey | exportKey | sign | verify
class ES256 extends ACryptoProvider {
    constructor() {
        super("ES256", "EC", "ECDSA", [], "SHA-512");
        this.webCryptoAlgSettings.namedCurve = "P-256";
    }
}

class ES384 extends ACryptoProvider {
    constructor() {
        super("ES384", "EC", "ECDSA", [], "SHA-3842");
        this.webCryptoAlgSettings.namedCurve = "P-384";
    }
}

class ES512 extends ACryptoProvider {
    constructor() {
        super("ES512", "EC", "ECDSA", [], "SHA-512");
        this.webCryptoAlgSettings.namedCurve = "P-512";
    }
}

//AES CTR WebCrypto usage: generateKey | importKey | exportKey | encrypt | decrypt | wrapKey | unwrapKey
class A128CTR extends ACryptoProvider {
    constructor() {
        super("A128CTR", "oct", "AES-CTR", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 128;
    }
}

class A192CTR extends ACryptoProvider {
    constructor() {
        super("A192CTR", "oct", "AES-CTR", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 192;
    }
}

class A256CTR extends ACryptoProvider {
    constructor() {
        super("A256CTR", "oct", "AES-CTR", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 256;
    }
}

//AES CBC WebCrypto usage: generateKey | importKey | exportKey | encrypt | decrypt | wrapKey | unwrapKey
class A128CBC extends ACryptoProvider {
    constructor() {
        super("A128CBC", "oct", "AES-CBC", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 128;
    }
}

class A192CBC extends ACryptoProvider {
    constructor() {
        super("A192CBC", "oct", "AES-CBC", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 192;
    }
}

class A256CBC extends ACryptoProvider {
    constructor() {
        super("A256CBC", "oct", "AES-CBC", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 256;
    }
}

//AES KW WebCrypto usage: generateKey | importKey | exportKey | wrapKey | unwrapKey
class A128KW extends ACryptoProvider {
    constructor() {
        super("A128KW", "oct", "AES-KW", []);
        this.webCryptoAlgSettings.length = 128;
    }
}

class A192KW extends ACryptoProvider {
    constructor() {
        super("A192KW", "oct", "AES-KW", []);
        this.webCryptoAlgSettings.length = 192;
    }
}

class A256KW extends ACryptoProvider {
    constructor() {
        super("A256KW", "oct", "AES-KW", []);
        this.webCryptoAlgSettings.length = 256;
    }
}

//AES GCM WebCrypto usage: generateKey | importKey | exportKey | encrypt | decrypt | wrapKey | unwrapKey
class A128GCM extends ACryptoProvider {
    constructor() {
        super("A128GCM", "oct", "AES-GCM", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 128;
    }
}

class A192GCM extends ACryptoProvider {
    constructor() {
        super("A192GCM", "oct", "AES-GCM", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 192;
    }
}

class A256GCM extends ACryptoProvider {
    constructor() {
        super("A256GCM", "oct", "AES-GCM", ["encrypt" /*"decrypt"*/]);
        this.webCryptoAlgSettings.length = 256;
    }
}

//AES GCM WebCrypto usage: generateKey | importKey | exportKey | wrapKey | unwrapKey
class A128GCMKW extends ACryptoProvider {
    constructor() {
        super("A128GCMKW", "oct", "AES-GCM", []);
        this.webCryptoAlgSettings.length = 128;
    }
}

class A192GCMKW extends ACryptoProvider {
    constructor() {
        super("A192GCMKW", "oct", "AES-GCM", []);
        this.webCryptoAlgSettings.length = 192;
    }
}

class A256GCMKW extends ACryptoProvider {
    constructor() {
        super("A256GCMKW", "oct", "AES-GCM", []);
        this.webCryptoAlgSettings.length = 256;
    }
}

//HMAC WebCrypto usage: generateKey | importKey | exportKey | sign | verify
class HS1 extends ACryptoProvider {
    constructor() {
        super("HS1", "oct", "HMAC", [], "SHA-1");
    }
}

class HS256 extends ACryptoProvider {
    constructor() {
        super("HS256", "oct", "HMAC", [], "SHA-256");
    }
}

class HS384 extends ACryptoProvider {
    constructor() {
        super("HS384", "oct", "HMAC", [], "SHA-384");
    }
}

class HS512 extends ACryptoProvider {
    constructor() {
        super("HS512", "oct", "HMAC", [], "SHA-512");
    }
}

EncryptionProviderManager.registerProvider(new RSA1_5());
EncryptionProviderManager.registerProvider(new RS256());
EncryptionProviderManager.registerProvider(new RS384());
EncryptionProviderManager.registerProvider(new RS512());
EncryptionProviderManager.registerProvider(new PS256());
EncryptionProviderManager.registerProvider(new PS384());
EncryptionProviderManager.registerProvider(new PS512());
EncryptionProviderManager.registerProvider(new RSA_OAEP());
EncryptionProviderManager.registerProvider(new RSA_OAEP_256());
EncryptionProviderManager.registerProvider(new RSA_OAEP_384());
EncryptionProviderManager.registerProvider(new RSA_OAEP_512());
EncryptionProviderManager.registerProvider(new ES256());
EncryptionProviderManager.registerProvider(new ES384());
EncryptionProviderManager.registerProvider(new ES512());
EncryptionProviderManager.registerProvider(new A128CTR());
EncryptionProviderManager.registerProvider(new A192CTR());
EncryptionProviderManager.registerProvider(new A256CTR());
EncryptionProviderManager.registerProvider(new A128CBC());
EncryptionProviderManager.registerProvider(new A192CBC());
EncryptionProviderManager.registerProvider(new A256CBC());
EncryptionProviderManager.registerProvider(new A128KW());
EncryptionProviderManager.registerProvider(new A192KW());
EncryptionProviderManager.registerProvider(new A256KW());
EncryptionProviderManager.registerProvider(new A128GCM());
EncryptionProviderManager.registerProvider(new A192GCM());
EncryptionProviderManager.registerProvider(new A256GCM());
EncryptionProviderManager.registerProvider(new A128GCMKW());
EncryptionProviderManager.registerProvider(new A192GCMKW());
EncryptionProviderManager.registerProvider(new A256GCMKW());
EncryptionProviderManager.registerProvider(new HS1());
EncryptionProviderManager.registerProvider(new HS256());
EncryptionProviderManager.registerProvider(new HS384());
EncryptionProviderManager.registerProvider(new HS512());
